import { ChatDetailsResponse } from '@choco-admin/types';
import { getInitials } from '@choco-admin/utils';
import {
  Button,
  Chip,
  Avatar,
  Tooltip,
  Checkbox,
  CheckboxGroup,
  Spinner,
} from '@heroui/react';
import React, { useState } from 'react';
import {
  FiEdit,
  FiMapPin,
  FiShoppingBag,
  FiExternalLink,
  FiAlertTriangle,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import {
  EntitySearchModal,
  type EntitySearchOption,
} from '../../../../../../../components/FormComponents';
import { useNotification } from '../../../../../../../contexts/NotificationContext';
import { useSearchBuyersForChat } from '../../../../../hooks/useSearchBuyersForChat';
import { useSearchBuyerUsers } from '../../../../../hooks/useSearchBuyerUsersForChat';
import {
  useUpdateChatBuyer,
  BuyerReplacedUsersNotAddedError,
} from '../../../../../hooks/useUpdateChatBuyer';
import { canEditBuyer } from '../../../../../utils/requestedChatLifecycle';
import { useChatPageContext } from '../../../contexts';

interface BuyerMetadata {
  email?: string;
  phone?: string;
  whatsApp?: boolean;
  sms?: boolean;
  fax?: boolean;
  orderPreferences?: { type: string }[];
  rolesEnabled?: boolean;
  fullAddress?: string;
  customerNumberMandatory?: boolean;
  referenceId?: string;
  name?: string;
}

type BuyerEntitySearchOption = EntitySearchOption & {
  metadata?: BuyerMetadata;
};

interface BuyerSectionProps {
  chatData: ChatDetailsResponse;
  chatId: string;
}

export const BuyerSection: React.FC<BuyerSectionProps> = ({
  chatData,
  chatId,
}) => {
  const { chatData: contextChatData, updateChatData } = useChatPageContext();
  const currentChatData = contextChatData || chatData;
  const currentBuyer = currentChatData.buyerEntity;
  const navigate = useNavigate();

  // Legacy parity: the buyer is editable for every status except `pending`
  // (withEditableBuyerSelect={chatStatus !== Pending}). Pending chats must not
  // reach the change-buyer path.
  const canChangeBuyer = canEditBuyer(currentChatData.status);

  const [isChangeBuyerModalOpen, setIsChangeBuyerModalOpen] = useState(false);
  const [selectedBuyerId, setSelectedBuyerId] = useState<string | null>(null);
  // Buyer users the operator explicitly chose to carry over. Starts empty:
  // replacing a buyer never silently re-adds every user of the new buyer.
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  const { addNotification } = useNotification();

  const {
    buyers,
    isLoading: searchLoading,
    searchBuyers,
  } = useSearchBuyersForChat();
  const { users: buyerUsers, isLoading: usersLoading } = useSearchBuyerUsers({
    buyerId: selectedBuyerId || '',
  });

  const closeAndReset = () => {
    setIsChangeBuyerModalOpen(false);
    setSelectedBuyerId(null);
    setSelectedUserIds([]);
  };

  const { updateChatBuyer, isUpdating } = useUpdateChatBuyer({
    onSuccess: () => {
      addNotification({
        title: 'Buyer Updated',
        message: 'The chat buyer has been updated successfully.',
        type: 'success',
      });
      closeAndReset();
    },
    onError: (error) => {
      // Distinguish a partial success (buyer switched, user adds failed) from a
      // real failure so the toast never understates what happened server-side.
      if (error instanceof BuyerReplacedUsersNotAddedError) {
        addNotification({
          title: 'Buyer Changed — Users Not Added',
          message: error.message,
          type: 'warning',
        });
        return;
      }
      addNotification({
        title: 'Update Failed',
        message: error.message,
        type: 'error',
      });
    },
  });

  const handleBuyerSelect = (selectedBuyer: BuyerEntitySearchOption) => {
    setSelectedBuyerId(selectedBuyer.id);
    // A different buyer means a different user set — start from an empty choice.
    setSelectedUserIds([]);
  };

  const handleConfirm = async (selectedBuyer: BuyerEntitySearchOption) => {
    if (!selectedBuyer.id) return;

    const nextBuyerEntity = {
      id: selectedBuyer.id,
      name: selectedBuyer.name,
      referenceId: selectedBuyer.metadata?.referenceId ?? null,
    };

    try {
      const reconciledUsers = await updateChatBuyer({
        chatId,
        buyerId: selectedBuyer.id,
        buyerUserIds: selectedUserIds,
      });

      // Full success: reflect server truth immediately — the new buyer entity
      // and the exact participant list the backend returned after the switch.
      updateChatData((prevData) =>
        prevData
          ? {
              ...prevData,
              buyerEntity: nextBuyerEntity,
              users: reconciledUsers,
            }
          : prevData
      );
    } catch (error) {
      if (error instanceof BuyerReplacedUsersNotAddedError) {
        // Partial success: the buyer switch committed server-side (clearing all
        // users) but the follow-up add failed. Still adopt the new buyer + the
        // empty, server-known user list so the card matches the server, then
        // swallow so the modal closes and the operator returns to the
        // participants list to re-add users (as the warning toast instructs).
        updateChatData((prevData) =>
          prevData
            ? { ...prevData, buyerEntity: nextBuyerEntity, users: [] }
            : prevData
        );
        return;
      }
      // Full failure: the buyer switch itself did not commit. Re-throw so the
      // modal surfaces the inline error and stays open for a retry; context is
      // left untouched (the old buyer is still correct).
      throw error;
    }
  };

  const handleCancel = () => {
    closeAndReset();
  };

  const handleBuyerClick = () => {
    if (currentBuyer?.id) {
      navigate(`/buyers/${currentBuyer.id}`);
    }
  };

  const renderBuyerDetails = (buyerEntity: BuyerEntitySearchOption) => (
    <div className="space-y-2">
      <h4 className="font-semibold text-foreground">{buyerEntity.name}</h4>
      {buyerEntity.metadata?.fullAddress && (
        <div className="flex items-start gap-2">
          <FiMapPin className="w-4 h-4 mt-0.5 text-default-500" />
          <p className="text-sm text-default-500">
            {buyerEntity.metadata.fullAddress}
          </p>
        </div>
      )}
      {buyerEntity.metadata?.referenceId && (
        <p className="text-sm text-default-500">
          ID: {buyerEntity.metadata.referenceId}
        </p>
      )}
    </div>
  );

  const renderUserSelection = () => {
    if (usersLoading) {
      return (
        <div className="flex items-center gap-2 text-sm text-default-500">
          <Spinner size="sm" aria-label="Loading buyer users" />
          Loading users...
        </div>
      );
    }

    if (buyerUsers.length === 0) {
      return (
        <p className="text-sm text-default-500">
          This buyer has no users to add.
        </p>
      );
    }

    return (
      <CheckboxGroup
        value={selectedUserIds}
        onValueChange={setSelectedUserIds}
        aria-label="Select buyer users to add"
      >
        {buyerUsers.map((user) => (
          <Checkbox key={user.value} value={user.value}>
            {user.label}
          </Checkbox>
        ))}
      </CheckboxGroup>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-default-500 tracking-wide flex items-center gap-2">
          <FiShoppingBag className="w-4 h-4" /> Primary Buyer
        </h4>
        <Tooltip
          content="The buyer cannot be changed while the chat is pending."
          isDisabled={canChangeBuyer}
        >
          <span>
            <Button
              size="sm"
              variant="flat"
              color="primary"
              startContent={<FiEdit className="w-4 h-4" />}
              onPress={() => {
                if (!canChangeBuyer) return;
                // Reset the buyer-user selection so the change-buyer modal opens
                // with no users pre-selected (explicit-selection contract, #122).
                setSelectedUserIds([]);
                setIsChangeBuyerModalOpen(true);
              }}
              isDisabled={!canChangeBuyer}
              data-testid="change-buyer-button"
            >
              Change Buyer
            </Button>
          </span>
        </Tooltip>
      </div>

      {/* Buyer Card */}
      {currentBuyer ? (
        <div
          className="group relative overflow-hidden rounded-xl border border-default-200 bg-content1 p-4 transition-all hover:border-primary/50 hover:shadow-md cursor-pointer"
          onClick={handleBuyerClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleBuyerClick();
            }
          }}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <Avatar
                name={getInitials(currentBuyer.name || 'B')}
                getInitials={(value) => value}
                className="h-12 w-12 text-lg shrink-0"
                isBordered
                color="primary"
                radius="lg"
              />
              <div>
                <h4 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors">
                  {currentBuyer.name}
                </h4>
                {currentBuyer.referenceId && (
                  <p className="text-sm text-default-500">
                    ID:{' '}
                    <span className="font-mono">
                      {currentBuyer.referenceId}
                    </span>
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Chip
                color="success"
                variant="flat"
                size="sm"
                className="capitalize"
              >
                Active
              </Chip>
              <Button
                isIconOnly
                size="sm"
                variant="light"
                className="opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <FiExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-default-300 bg-default-50 p-6 text-center">
          <FiShoppingBag className="mx-auto h-8 w-8 text-default-300 mb-2" />
          <p className="text-default-500">No buyer selected</p>
        </div>
      )}

      {/* Warning Box */}
      <div className="rounded-xl border border-warning-200 bg-warning-50/50 dark:bg-warning-900/10 p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-warning-100 p-2 text-warning-600 dark:bg-warning-900/30 dark:text-warning-400">
            <FiAlertTriangle className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <h5 className="text-sm font-semibold text-warning-800 dark:text-warning-200">
              Changing Buyer Entity
            </h5>
            <p className="mt-1 text-sm text-warning-700 dark:text-warning-300 leading-relaxed">
              Changing the buyer entity immediately clears all users (both buyer
              and distributor) from this chat. Pick exactly which buyer users to
              re-add — none are added automatically.
            </p>
          </div>
        </div>
      </div>

      <EntitySearchModal
        isOpen={isChangeBuyerModalOpen}
        onClose={handleCancel}
        title="Change Chat Buyer"
        searchPlaceholder="Search buyers by name or address..."
        entityType="buyer"
        isSearching={searchLoading}
        searchResults={buyers.map((buyerOption) => ({
          id: buyerOption.value,
          name: buyerOption.label,
          label: buyerOption.label,
          subtitle: buyerOption.metaData?.fullAddress || '',
          metadata: {
            fullAddress: buyerOption.metaData?.fullAddress || '',
            name: buyerOption.metaData?.name || '',
            referenceId: buyerOption.metaData?.referenceId || '',
          },
        }))}
        onSearch={searchBuyers}
        onConfirm={handleConfirm}
        isConfirming={isUpdating}
        onSelect={handleBuyerSelect}
        renderConfirmation={(selectedBuyer) => (
          <div className="space-y-4">
            <div className="p-4 bg-primary-50 rounded-lg">
              {renderBuyerDetails(selectedBuyer)}
            </div>

            <div className="p-4 bg-content2 rounded-lg space-y-3">
              <div>
                <h4 className="font-semibold text-foreground">
                  Select users to add
                </h4>
                <p className="text-xs text-default-500">
                  Only the users you check are added to the chat. Leave all
                  unchecked to change the buyer without adding any users.
                </p>
              </div>
              {renderUserSelection()}
            </div>
          </div>
        )}
      />
    </div>
  );
};
